<template>
  <div
    class="p-5 page-wrapper"
  >
    <section
      class="mb-2"
    >
      <b-row
        class="gutters-0 new-gutters-xs"
      >
        <b-col>
          <base-help-tooltip>
            <template v-slot:left>
              <div>
                <h2 class="page-title">
                  Landing Pages
                </h2>
              </div>
            </template>
            <template v-slot:popover-body>
              <p>This is where you manage the landing pages your campaign promotes.</p>
              <p> Upload, edit, publish, or delete content here. Our system analyses each landing page to automatically generate and optimise individual display ads.</p>
              <p>Changes made here will directly affect how your ads appear and match on publisher sites when the campaign is live.</p>
            </template>
          </base-help-tooltip>
        </b-col>
      </b-row>
    </section>
    <b-row
      class="mb-4 gutters-0"
    >
      <b-col
        cols="8"
      >
        <articles-filters
          @fetchArticles="fetchArticles(...arguments)"
          @resetSelectedIds="toggleAllArticlesSelection(false)"
        />
      </b-col>
      <b-col
        cols="auto"
        class="ml-auto"
      >
        <b-button
          variant="secondary"
          size="sm"
          class="mr-2"
          @click="openUploadContentModal()"
          :disabled="!canAddArticles"
          data-test="add-article-btn"
        >
          Add URL <i class="fal fa-plus" />
        </b-button>
        <b-button
          variant="secondary"
          size="sm"
          :class="{ 'btn-loading': isExportingCSV }"
          @click="exportAsCSV()"
          :disabled="!canDownloadAsCSV"
        >
          Export as CSV <i class="fal fa-download" />
        </b-button>
      </b-col>
    </b-row>
    <section
      class="w-75 mb-4"
    >
      <upload-progress-animation-box />
    </section>
    <section v-if="areArticlesSelected">
      <span class="tag tag-cyan-light">
        {{ articlesSelectedAmount }} item{{ articlesSelectedAmount > 1 ? 's' : '' }} selected
      </span>
    </section>
    <section>
      <b-table
        tbody-tr-class="table-row"
        :fields="contentTableHeaders"
        :items="articles"
        :current-page="paging.currentPage"
        :per-page="getPerPageSelected"
        :busy="isLoadingArticles"
        @sort-changed="sortTable($event)"
        sort-by="title"
        no-local-sorting
        hover
        show-empty
      >
        <template v-slot:table-colgroup="scope">
          <col
            v-for="field in scope.fields"
            :key="field.key"
            :class="`column column-${field.key}`"
          >
        </template>
        <template v-slot:head(selected)="data">
          <div>
            <b-form-checkbox
              :checked="areArticlesSelected"
              @input="selectAll($event)"
              :class="[{ 'cursor-not-allowed' : !canSelectArticles }]"
              :disabled="!canSelectArticles"
              class="d-inline"
            />
            <select-all-options
              :selected-type="selectType"
              @updateSelectOption="updateSelectType"
            />
          </div>
        </template>
        <template v-slot:head(title)="data">
          <div
            ref="article-title-container"
          >
            <articles-bulk-actions
              v-if="areArticlesSelected"
              :selected-ids="selectedArticlesIds"
              :are-all-articles-selected="areAllArticlesSelected"
              @openActionConfirmationModal="openActionConfirmationModal(...arguments)"
              @openScheduleArticlesModal="openScheduleArticlesModal(...arguments)"
            />
            <p
              v-else
              class="mb-0"
            >
              {{ data.label }}
            </p>
          </div>
        </template>
        <template v-slot:cell(selected)="data">
          <b-form-checkbox
            v-if="!isArticleUploading(data.item)"
            :checked="isArticleSelected(data.item.id)"
            @input="toggleArticleSelection($event, data.item.id)"
            :disabled="!canSelectArticles"
            :data-test="`article-selection-${data.index}`"
          />
        </template>
        <template v-slot:cell(title)="data">
          <div
            class="d-flex align-items-center"
          >
            <div
              v-if="shouldDisplayImage"
              class="article-image-container mr-4"
            >
              <span
                v-if="data.item.imageUrl === 'loading'"
                :class="['article-image ', { 'loader': data.item.imageUrl === 'loading' }]"
              />
              <img
                v-else
                class="article-image"
                :src="data.item.imageUrl || '/images/image-not-set.png'"
                data-test="article-image"
              >
            </div>
            <template
              v-if="isArticleSuccessful(data.item)"
            >
              <div>
                <p class="ellipsis mb-0 word-break-all">
                  {{ data.item.title }}
                </p>
                <p class="ellipsis mb-0 word-break-all">
                  <a :href="data.item.url">
                    {{ data.item.url }}
                  </a>
                </p>
              </div>
            </template>
            <template
              v-else
            >
              <p class="ellipsis mb-0 word-break-all text-muted'">
                {{ data.item.url }}
              </p>
            </template>
          </div>
        </template>
        <template v-slot:cell(actions)="data">
          <articles-actions
            :article="data.item"
            @openActionConfirmationModal="openActionConfirmationModal(...arguments)"
            @openScheduleArticlesModal="openScheduleArticlesModal(...arguments)"
            :index="data.index"
          />
        </template>
        <template v-slot:cell(state)="data">
          <p
            :class="`text-nowrap mb-0 ${getStateClass(data.item.state)}`"
          >
            {{ data.item.state }}
          </p>
        </template>
        <template v-slot:cell(language)="data">
          <p
            v-if="!isArticleUploading(data.item) && data.item.hasOwnProperty('languageId')"
            class="text-nowrap mb-0"
          >
            {{ getArticleLanguage(data.item.languageId).name }}
          </p>
        </template>
        <template v-slot:cell(createdDate)="data">
          <p
            v-if="!isArticleUploading(data.item)"
            class="text-nowrap mb-0"
          >
            {{ formatDateForPlatform(data.item.createdDate) }}
          </p>
        </template>
        <template v-slot:cell(updatedDate)="data">
          <p
            v-if="!isArticleUploading(data.item)"
            class="text-nowrap mb-0"
          >
            {{ formatDateForPlatform(data.item.updatedDate) }}
          </p>
        </template>
        <template v-slot:cell(matchingHealth)="data">
          <div class="d-flex align-items-center justify-content-center">
            <span
              class="icon"
            >
              <i
                v-if="data.item.state === 'FAILED'"
                style="font-size: 1.5rem;"
                class="fas fa-times-circle error"
              />
              <i
                :class="`${getMatchingHealthStatusIcon(data.item.matchingHealth)}`"
                style="font-size: 1.5rem;"
              />
            </span>
          </div>
        </template>
        <template v-slot:table-busy>
          <div
            class="loader m-auto"
          />
        </template>
        <template v-slot:empty>
          <div
            v-if="scrapedContent.length === 0"
            class="py-3"
          >
            <p
              class="mb-5 text-center"
            >
              Your campaign has no landing page URLs. <br>
              Please add destination URLs where your campaign traffic will be directed. <br>
              These URLs are required to proceed with campaign setup.
            </p>
            <b-button
              variant="primary"
              size="lg"
              class="d-block mx-auto"
              data-test="add-campaign-content-btn"
              @click="openUploadContentModal()"
            >
              Add Landing Pages
            </b-button>
          </div>
          <b-alert
            v-else
            show
            variant="warning"
            class="w-50 mx-auto text-center mb-0"
          >
            Sorry, you don't seem to have any articles that match your search.
          </b-alert>
        </template>
      </b-table>
    </section>
    <section>
      <base-pagination
        data-test="content-info-pagination"
        :items-count="pagingTotalItemsCount"
        :current-page="paging.currentPage"
        :per-page-selected="paging.perPageSelected"
        items-name="content items"
        @pagingUpdate="updatePaging(...arguments)"
        :disabled="paging.isDisabled"
      />
    </section>
    <section>
      <p
        v-if="feedId"
        :class="[[GET_IS_VIEW_TYPE('Editor') ? 'pb-4' : 'pb-9'], 'mb-0 text-muted small']"
      >
        Feed ID: {{ feedId }}
      </p>
    </section>
    <content-upload-modal
      @fetchArticles="fetchArticles()"
    />
    <schedule-articles-modal
      :custom-props="scheduleModalCustomProps"
      @confirmAction="confirmAction()"
    />
    <actions-confirmation-modal
      :custom-props="confirmationModalCustomProps"
      @confirmAction="confirmAction()"
      @resetCustomProps="resetCustomProps()"
    />
  </div>
</template>
<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as contentTypes from '@/modules/content/store/types';
import * as accountTypes from '@/modules/account/store/types';

import ArticlesBulkActions from '@/modules/content/components/ContentPage/ArticlesBulkActions';
import ArticlesActions from '@/modules/content/components/ContentPage/ArticlesActions';

import helper from '@/core/helper';
import dateHelper from '@/core/mixins/dateHelper';
import fileHelper from '@/core/mixins/fileHelper';
import textHelper from '@/core/mixins/textHelper';

import ArticlesStateMixins from '@/modules/content/components/ContentPage/mixins/ArticlesStateMixins';
import ArticlesActionsMixins from '@/modules/content/components/ContentPage/mixins/ArticlesActionsMixins';

import UploadProgressAnimationBox from '@/modules/content/components/Articles/UploadProgressAnimationBox.vue';
import BasePagination from '@/components/BasePagination';
import ArticlesFilters from '@/modules/content/components/ContentPage/ArticlesFilters';
import ContentUploadModal from '@/modules/content/components/ContentPage/ContentUploadModal';
import ScheduleArticlesModal from '@/modules/content/components/ContentPage/ScheduleArticlesModal';
import ActionsConfirmationModal from '@/modules/content/components/ContentPage/ActionsConfirmationModal';
import SelectAllOptions from '@/modules/content/components/ContentPage/SelectAllActions';

function initialData() {
  return {
    isExportingCSV: false,
    resizeObserver: null,
    shouldDisplayImage: true,
    paging: {
      isDisabled: false,
      currentPage: 1,
      perPageSelected: 20,
    },
    isSelectAllChecked: false,
    selectedArticlesIds: [],
    selectType: 'PAGE',
    confirmationModalCustomProps: {},
    scheduleModalCustomProps: {},
  };
}
export default {
  name: 'ContentInfo',
  mixins: [
    dateHelper,
    fileHelper,
    textHelper,
    ArticlesStateMixins,
    ArticlesActionsMixins,
  ],
  components: {
    SelectAllOptions,
    ArticlesFilters,
    ArticlesBulkActions,
    ArticlesActions,
    UploadProgressAnimationBox,
    BasePagination,
    ContentUploadModal,
    ScheduleArticlesModal,
    ActionsConfirmationModal,
  },
  data() {
    return initialData();
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
      'selectedAdvertiser',
    ]),
    ...mapState('content', {
      scrapedContent: 'newScrapedContent',
      contentItems: 'newContentItems',
    }),
    ...mapState('content', [
      'totalItems',
      'languages',
      'contentItemsQueryParams',
      'isLoadingArticles',
      'feedId',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_VIEW_TYPE,
      campaignTypes.GET_IS_SELF_SERVICE_CAMPAIGN_ACCESSED_BY_ADMIN,
      campaignTypes.GET_IS_CAMPAIGN_READONLY,
      campaignTypes.GET_IS_CAMPAIGN_ENDED,
    ]),
    ...mapGetters('content', [
      contentTypes.GET_IN_PROGRESS_CONTENT_ITEMS,
      contentTypes.GET_FAILED_SCRAPED_CONTENT_ITEMS,
      contentTypes.GET_SUCCESSFULLY_SCRAPED_CONTENT_ITEMS,
    ]),
    ...mapGetters('account', [
      accountTypes.GET_IS_MANAGED_ACCOUNT,
    ]),
    areAllArticlesSelected() {
      return this.isSelectAllChecked && this.selectType === 'ALL';
    },
    isAllArticlesPerPageSelected() {
      return this.isSelectAllChecked && this.selectType === 'PAGE';
    },
    canAddArticles() {
      return !this.GET_IS_CAMPAIGN_READONLY
        && this.GET_IN_PROGRESS_CONTENT_ITEMS.length === 0;
    },
    contentTableHeaders() {
      return [
        { key: 'selected', label: 'SELECTED', sortable: false, thStyle: { width: '60px' } },
        { key: 'title', label: 'LANDING PAGES', sortable: this.scrapedContent.length > 0 && !this.isFilteredState('FAILED') },
        { key: 'actions', label: 'ACTIONS', sortable: false },
        { key: 'state', label: 'STATE' },
        { key: 'language', label: 'LANGUAGE', sortable: false },
        {
          key: 'createdDate', label: 'CREATED', sortable: this.scrapedContent.length > 0 && !this.isFilteredState('FAILED'),
        },
        {
          key: 'updatedDate', label: 'LAST UPDATED', sortable: this.scrapedContent.length > 0 && !this.isFilteredState('FAILED'),
        },
        { key: 'matchingHealth', label: 'MATCHING HEALTH', sortable: false },
      ];
    },
    pagingTotalItemsCount() {
      if (!this.areFiltersApplied) return this.scrapedContent.length;
      return this.isFilteredState('FAILED') ? this.GET_FAILED_SCRAPED_CONTENT_ITEMS.length : this.totalItems;
    },
    areFiltersApplied() {
      return this.contentItemsQueryParams.hasOwnProperty('search')
        || this.contentItemsQueryParams.hasOwnProperty('states')
        || this.contentItemsQueryParams.hasOwnProperty('languageId')
        || this.contentItemsQueryParams.hasOwnProperty('matchingHealth');
    },
    hasOffsetItems() {
      return (this.GET_IN_PROGRESS_CONTENT_ITEMS.length
        + this.GET_FAILED_SCRAPED_CONTENT_ITEMS.length) > (this.paging.currentPage - 1) * this.paging.perPageSelected;
    },
    articles() {
      if (this.isFilteredState('FAILED')) return this.GET_FAILED_SCRAPED_CONTENT_ITEMS;
      if (this.areFiltersApplied || !this.hasOffsetItems) return this.GET_SUCCESSFULLY_SCRAPED_CONTENT_ITEMS;
      return this.GET_IN_PROGRESS_CONTENT_ITEMS
        .concat(this.GET_FAILED_SCRAPED_CONTENT_ITEMS)
        .concat(this.GET_SUCCESSFULLY_SCRAPED_CONTENT_ITEMS);
    },
    articlesSelectedAmount() {
      if (this.areAllArticlesSelected) return this.pagingTotalItemsCount;
      return this.selectedArticlesIds.length;
    },
    areArticlesSelected() {
      return this.selectedArticlesIds.length > 0;
    },
    canSelectArticles() {
      return !this.GET_IS_CAMPAIGN_ENDED;
    },
    canDownloadAsCSV() {
      return this.GET_SUCCESSFULLY_SCRAPED_CONTENT_ITEMS.length !== 0;
    },
    getPerPageSelected() {
      return this.hasOffsetItems ? this.paging.perPageSelected : 0;
    },
  },
  methods: {
    ...mapActions('campaigns', [
      campaignTypes.FETCH_ADVERTISER,
    ]),
    ...mapActions('content', [
      contentTypes.FETCH_FEED,
      contentTypes.EXPORT_ARTICLES_AS_CSV,
      contentTypes.SET_CONTENT_ITEM_QUERY_PARAM,
      contentTypes.RESET_CONTENT_ITEMS_QUERY_PARAMS,
      contentTypes.FETCH_ARTICLES,
    ]),
    selectAll(isChecked) {
      if (isChecked) {
        this.toggleAllArticlesSelection(true);
      } else {
        this.toggleAllArticlesSelection(false);
      }
    },
    toggleAllArticlesSelection(isChecked) {
      if (!isChecked) {
        this.selectedArticlesIds = [];
        this.isSelectAllChecked = false;
        return;
      }
      if (this.selectedArticlesIds.length === 0) {
        this.isSelectAllChecked = true;
        if (this.selectType === 'PAGE') {
          this.selectedArticlesIds = this.articles.map((article) => article.id);
        } else this.selectedArticlesIds = [];
      }
    },
    isArticleSelected(id) {
      return this.selectedArticlesIds.includes(id)
        || this.areAllArticlesSelected;
    },
    toggleArticleSelection(isChecked, id) {
      if (isChecked) {
        if (this.selectedArticlesIds.includes(id)) return;
        this.selectedArticlesIds.push(id);
        if (this.articles.length < this.paging.perPageSelected
          && this.selectedArticlesIds.length === this.articles.length) {
          this.isSelectAllChecked = true;
        }
      } else {
        this.selectedArticlesIds.splice(this.selectedArticlesIds.indexOf(id), 1);
        if (this.isSelectAllChecked) this.isSelectAllChecked = false;
      }
    },
    openActionConfirmationModal(payload) {
      this.confirmationModalCustomProps = Object.assign({}, payload);
      this.$root.$emit('bv::show::modal', 'actions-confirm-modal');
    },
    async confirmAction() {
      await this.fetchArticles();
      this.toggleAllArticlesSelection(false);
    },
    resetCustomProps() {
      this.confirmationModalCustomProps = {};
    },
    openScheduleArticlesModal(payload) {
      this.scheduleModalCustomProps = Object.assign({}, payload);
      this.$root.$emit('bv::show::modal', 'schedule-articles-modal');
    },
    async sortTable(event) {
      if (!event.sortBy) return;
      const sortOrder = event.sortOrder ?? event.sortDesc ? 'desc' : 'asc';
      await this.SET_CONTENT_ITEM_QUERY_PARAM({
        page: 1,
        sort: `${event.sortBy},${sortOrder}`,
      });
      const articles = await this.fetchArticles();
      if (this.isAllArticlesPerPageSelected) {
        this.selectedArticlesIds = this.articles.map((article) => article.id);
      } else if (!this.areAllArticlesSelected) {
        this.selectedArticlesIds = [];
      }
      return articles;
    },
    async fetchArticles(resetPaging = true) {
      this.setPaging({ isDisabled: true });
      return this.FETCH_ARTICLES({ offset: !this.areFiltersApplied })
        .then(() => {
          if (resetPaging) this.setPaging({ currentPage: 1 });
          this.setPaging({ isDisabled: false });
        });
    },
    getStateClass(state) {
      const stateList = {
        INITIALISED: 'label label-secondary px-2',
        'IN PROGRESS': 'label label-secondary px-2',
        FAILED: 'label label-danger px-2',
        DRAFT: 'label label-dark px-2',
        SCHEDULED: 'label label-blue px-2',
        PUBLISHED: 'label label-success px-2',
        PAUSED: 'label label-warning px-2',
      };
      if (!stateList.hasOwnProperty(state)) return;
      return stateList[state];
    },
    getArticleLanguage(languageId) {
      return this.languages.find((language) => language.id === languageId);
    },
    getMatchingHealthStatusIcon(score) {
      switch (score) {
        case 'HIGH':
          return 'fas fa-check-circle success';
        case 'MEDIUM':
          return 'fas fa-exclamation-triangle warning';
        case 'LOW':
          return 'fas fa-times-circle error';
        default:
          return 'spinner';
      }
    },
    openUploadContentModal() {
      if (!this.canAddArticles) return;
      this.$root.$emit('bv::show::modal', 'content-upload-modal');
    },
    setIsValidForNavigation(isValid) {
      this.$emit('setIsValidForNavigation', 'next', isValid);
    },
    async updatePaging(payload) {
      const isSameCurrentPage = payload.page === this.paging.currentPage;
      const isSamePerPageSelected = payload.perPageSelected === this.paging.perPageSelected;

      if (isSameCurrentPage && isSamePerPageSelected) return;
      this.setPaging({
        currentPage: payload.page,
        perPageSelected: payload.perPageSelected,
      });
      await this.SET_CONTENT_ITEM_QUERY_PARAM({
        page: isSamePerPageSelected ? payload.page : 1,
        pageSize: payload.perPageSelected,
      });
      await this.fetchArticles(isSameCurrentPage);
      if (this.isAllArticlesPerPageSelected) {
        this.selectedArticlesIds = this.articles.map((article) => article.id);
      } else if (!this.areAllArticlesSelected) {
        const articlesIdsOnThePage = this.articles.map((article) => article.id);
        const areThereAnyMissingIds = this.selectedArticlesIds.some((id) => !articlesIdsOnThePage.includes(id));
        if (areThereAnyMissingIds) {
          this.selectedArticlesIds = [];
          this.isSelectAllChecked = false;
        }
      }
    },
    async exportAsCSV() {
      this.isExportingCSV = true;
      const payload = {
        feedId: this.feedId,
        contentItemIds: this.selectedArticlesIds,
      };
      if (this.selectedArticlesIds.length === 0 || this.areAllArticlesSelected) delete payload.contentItemIds;
      const csvContent = await this.EXPORT_ARTICLES_AS_CSV(payload);
      this.isExportingCSV = false;
      const fileName = `${this.campaign.name} - Landing Page List - ${this.convertDateToString(new Date())}`;
      this.downloadCsvFile(csvContent, fileName);
    },
    setPaging(payload) {
      if (helper.objLength(payload).length === 0) return;
      for (const property in payload) {
        if (!payload.hasOwnProperty(property)) continue;
        if (this.paging.hasOwnProperty(property)) this.paging[property] = payload[property];
      }
    },
    attachResizeObserver() {
      this.detachResizeObserver();
      this.resizeObserver = new ResizeObserver((entries) => {
        const articleTitleContainer = entries[0];
        const isLargeEnough = articleTitleContainer.contentRect.width > 250;
        this.shouldDisplayImage = isLargeEnough;
      });
      this.resizeObserver.observe(this.$refs['article-title-container']);
    },
    detachResizeObserver() {
      if (this.resizeObserver instanceof ResizeObserver) {
        this.resizeObserver.unobserve(this.$refs['article-title-container']);
      }
    },
    updateSelectType(updatedSelectType) {
      if (this.areAllArticlesSelected && updatedSelectType === 'PAGE') {
        this.selectedArticlesIds = this.articles.map((article) => article.id);
      }
      this.selectType = updatedSelectType;
    },
  },
  async mounted() {
    this.FETCH_ADVERTISER(this.campaign.advertiserId)
      .then(() => {
        this.FETCH_FEED({
          advertiserId: this.selectedAdvertiser.id,
          feedId: this.feedId,
        });
      });
    this.setIsValidForNavigation(false);
    await this.SET_CONTENT_ITEM_QUERY_PARAM({
      page: this.paging.currentPage,
      pageSize: this.paging.perPageSelected,
    });
    await this.sortTable({
      sortBy: 'title',
    });
    this.setIsValidForNavigation(this.scrapedContent.some((item) => item.status === 'COMPLETED' || item.status === 'MANUALLY_ADDED'));
    this.attachResizeObserver();
  },
  beforeDestroy() {
    this.RESET_CONTENT_ITEMS_QUERY_PARAMS();
    this.detachResizeObserver();
  },
  watch: {
    scrapedContent: {
      deep: true,
      handler(newVal) {
        if (newVal.length === 0) this.setIsValidForNavigation(false);
        if (newVal.length > 0 && newVal.some((item) => item.status === 'COMPLETED')) this.setIsValidForNavigation(true);
      },
    },
  },
};
</script>
<style lang="scss" scoped>
input[type=radio] {
  transform: scale(1.25);
}
.article-image-container {
  flex-shrink: 0;
  width: 100px;
  height: 68px;
}
.article-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.action-button {
  display: flex;
  justify-content: center;
  align-items: center;
  .icon-btn {
    font-weight: 400;
    font-size: 1rem;
  }
}
::v-deep {
  .table-row {
    background-color: $white;
    border: $border-width solid $border-color;
    height: 93px;
  }
  .table td {
  vertical-align: middle;
  }
  .column-title {
    min-width: 290px;
  }
  .column-actions {
    min-width: 152px;
  }
}
</style>
